/**
 * geoSessionApi.ts
 *
 * Thin API layer for the BFF geo-session endpoint.
 * Mints a short-lived opaque token the browser attaches as `X-Geo-Token` on
 * every /api/v1/* request to GeoCodingMapService / RoutingMapService.
 *
 * Token lifetime: configured on BFF (default 15 min, clamped to primary session TTL).
 * GeoSessionProvider is responsible for proactive refresh before expiry.
 *
 * Endpoint: POST /drt/api/bff/geo-session
 * Response: { token: string; expiresAt: string }   (ISO-8601 expiry)
 *
 * Mirrors projectionSessionApi.ts — same native-fetch approach so this function
 * is safe to call both inside React hooks and from non-React interceptors.
 */

import { apiRoutes } from '@/shared/api/routes';

export interface GeoSessionResponse {
  /** Short-lived opaque geo-access token */
  token: string;
  /** ISO-8601 timestamp when the token expires */
  expiresAt: string;
  /**
   * The tenantId the BFF embedded in the GeoSessionRecord at mint time.
   * The maps proxy validates X-Tenant-ID against this value — the FE MUST
   * echo it back verbatim.  Do NOT use the value from useTenantContext();
   * the BFF session context is authoritative.
   */
  tenantId?: string;
}

/**
 * Fetches a new geo-session token from the BFF.
 *
 * Uses native fetch (not apiClient / Axios) to avoid circular dependency:
 * the Axios interceptor in client.ts reads the token from geoTokenStore —
 * using apiClient here would create a cycle. Session cookie is included
 * automatically via `credentials: 'include'`.
 */
export const fetchGeoSession = async (): Promise<GeoSessionResponse> => {
  const url = apiRoutes.bff('geo-session');
  const response = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    throw new Error(
      `[GeoSession] Failed to mint token: HTTP ${response.status} ${response.statusText}`,
    );
  }

  const data = (await response.json()) as unknown;

  if (
    typeof data !== 'object' ||
    data === null ||
    typeof (data as Record<string, unknown>).token !== 'string'
  ) {
    throw new Error('[GeoSession] Unexpected response shape — missing token field');
  }

  return data as GeoSessionResponse;
};
